import React, { useState, useEffect } from 'react';
import { mockPipelineService } from '@/services/mock/MockPipelineService';
import { PipelineFilterState, PipelineMetricsSummary, PipelineRecord, PipelineStageKey } from '@/types/domain/pipeline';
import { PipelineMetricsHeader } from '@/components/pipeline/PipelineMetricsHeader';
import { PipelineFilters } from '@/components/pipeline/PipelineFilters';
import { PipelineTableView } from '@/components/pipeline/PipelineTableView';
import { Activity, Layers } from 'lucide-react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';

const STAGE_QUICK_TABS: { key: PipelineStageKey | 'ALL'; label: string }[] = [
  { key: 'ALL', label: 'All Lifecycle' },
  { key: 'REQUEST', label: '1. Requisition' },
  { key: 'APPROVAL', label: '2. NFA Approval' },
  { key: 'SAP_PR', label: '3. SAP PR Sync' },
  { key: 'RFQ', label: '4. RFQ Sourcing' },
  { key: 'QUOTATION', label: '5. Quotations' },
  { key: 'SUPPLIER', label: '6. Vendor Award' },
  { key: 'PO', label: '7. Purchase Order' },
  { key: 'DELIVERY', label: '8. Delivery & GRN' },
  { key: 'INVOICE', label: '9. 3-Way Match & Pay' },
];

export const PipelinePage: React.FC = () => {
  const [metrics, setMetrics] = useState<PipelineMetricsSummary | null>(null);
  const [records, setRecords] = useState<PipelineRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [filters, setFilters] = useState<PipelineFilterState>({
    search: '',
    stage: 'ALL',
    status: 'ALL',
    department: 'ALL',
    plant: 'ALL',
    category: 'ALL',
    priority: 'ALL',
    isDelayedOnly: false,
    isBlockedOnly: false,
    page: 1,
    pageSize: 100,
    viewMode: 'TABLE',
  });

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [m, r] = await Promise.all([
        mockPipelineService.getPipelineMetrics(),
        mockPipelineService.getPipelineRecords(filters),
      ]);
      setMetrics(m);
      setRecords(r.items);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filters]);

  const handleFilterChange = (updated: Partial<PipelineFilterState>) => {
    setFilters((prev) => ({ ...prev, ...updated }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      stage: 'ALL',
      status: 'ALL',
      department: 'ALL',
      plant: 'ALL',
      category: 'ALL',
      priority: 'ALL',
      isDelayedOnly: false,
      isBlockedOnly: false,
      page: 1,
      pageSize: 100,
      viewMode: 'TABLE',
    });
  };

  const handleRetrySapSync = async (id: string) => {
    try {
      await mockPipelineService.retrySapSync(id);
      await loadData();
    } catch {
      alert('Failed to retry SAP sync.');
    }
  };

  return (
    <div className="space-y-6 pb-12 font-poppins">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5 font-poppins">
            <Activity className="h-7 w-7 text-primary" /> Procurement Pipeline Command Center
          </h1>
          <p className="text-xs text-foreground-secondary font-medium mt-1 font-poppins">
            End-to-end lifecycle oversight from initial requisition to managerial approval, SAP PR sync, RFQ sourcing, PO issue, GRN inspection, and 3-way invoice settlement.
          </p>
        </div>
      </div>

      {/* Top 6 KPI Metric Cards */}
      {metrics && (
        <PipelineMetricsHeader
          metrics={metrics}
          onFilterClick={(type) => {
            if (type === 'DELAYED') handleFilterChange({ isDelayedOnly: true, isBlockedOnly: false });
            else if (type === 'SAP_EXCEPTION') handleFilterChange({ isBlockedOnly: true, isDelayedOnly: false });
            else handleResetFilters();
          }}
        />
      )}

      {/* Squeezed Poppins Filters Toolbar */}
      <PipelineFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
      />

      {/* Main Enterprise Pipeline Ledger */}
      <Card className="border-border rounded-2xl shadow-xs overflow-hidden font-poppins">
        <CardHeader className="p-4 border-b border-border/60 bg-muted/20">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-bold text-foreground font-poppins">
              <Layers className="h-4 w-4 text-primary" />
              <span>Operational Stage Fast-Filter</span>
            </div>

            {/* Stage Filter Pills */}
            <div className="flex flex-wrap items-center gap-1.5 overflow-x-auto custom-scrollbar pb-1 lg:pb-0">
              {STAGE_QUICK_TABS.map((tab) => {
                const isActive = filters.stage === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => handleFilterChange({ stage: tab.key })}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap font-poppins ${
                      isActive
                        ? 'bg-primary text-primary-foreground font-bold shadow-xs'
                        : 'bg-muted/70 text-foreground-secondary hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4 overflow-x-hidden">
          <PipelineTableView
            records={records}
            isLoading={isLoading}
            onRetrySapSync={handleRetrySapSync}
            onClearFilters={handleResetFilters}
          />
        </CardContent>
      </Card>
    </div>
  );
};
